import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { openWhatsApp, scrollToId } from "@/lib/api";

const links = [
  { label: "Collection", id: "collection" },
  { label: "Our Story", id: "story" },
  { label: "Reviews", id: "reviews" },
  { label: "Visit", id: "visit" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 0.1, ease: [0.19, 1, 0.22, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "backdrop-blur-xl bg-[#0C0A09]/70 border-b border-[#292524]" : "bg-transparent"
      }`}
      data-testid="main-nav"
    >
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <button
          onClick={() => window.__lenis ? window.__lenis.scrollTo(0) : window.scrollTo({ top: 0, behavior: "smooth" })}
          className="font-serif text-xl sm:text-2xl tracking-tight text-[#F5F5F0]"
          data-testid="nav-logo"
        >
          Ave<span className="text-[#C4A47C]">.</span>luxe
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              className="label link-underline text-[#F5F5F0]/80 hover:text-[#F5F5F0] transition-colors"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <button
          onClick={() => openWhatsApp("Hi Ave_luxe_candles! I'd love to know more about your candles.")}
          className="hidden md:inline-flex items-center gap-2 rounded-full bg-[#C4A47C] hover:bg-[#D8BC97] text-[#0C0A09] px-5 py-2.5 text-sm font-medium transition-colors"
          data-testid="nav-whatsapp-btn"
        >
          <MessageCircle size={16} strokeWidth={1.5} />
          Order on WhatsApp
        </button>

        <button
          className="md:hidden text-[#F5F5F0]"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-[#0C0A09]/95 backdrop-blur-xl border-b border-[#292524]"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="label text-left text-[#F5F5F0]"
                  data-testid={`nav-mobile-link-${l.id}`}
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => openWhatsApp("Hi Ave_luxe_candles! I'd love to know more about your candles.")}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C4A47C] text-[#0C0A09] px-5 py-3 text-sm font-medium"
                data-testid="nav-mobile-whatsapp-btn"
              >
                <MessageCircle size={16} strokeWidth={1.5} /> Order on WhatsApp
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
